// Die Übergabe: den laufenden Sender an das nächste Studio weitergeben,
// ohne dass es jemand hört.
//
// Der Kanal hat zwei Sendeplätze. Beide Studios dürfen gleichzeitig verbunden
// sein, zu hören ist nur einer. Der Ablauf, der sich im Betrieb bewährt hat:
//
//   1. Der Nachfolger hängt sich auf den freien Platz (Zugangsdaten stehen hier).
//   2. Wer auf Sendung ist, drückt **Übergabe anbieten** – das ist die
//      Freigabe, nicht die Umschaltung.
//   3. Einer von beiden drückt **Jetzt übergeben**. Für ein paar Sekunden sind
//      beide zu hören, dann nur noch der Neue.
//
// Ohne Angebot übernimmt niemand: Sonst könnte jemand mitten im Satz die
// Sendung wegnehmen. Die Leitung darf immer – wenn ein Rechner abstürzt, muss
// jemand rankommen.

import { useCallback, useEffect, useState } from "react";
import { api, type Uebergabe } from "../api";
import { errorText } from "./streaming-ui";

/** „Platz A" – so heißen sie auch in der Anleitung. */
export function platzName(platz: "a" | "b"): string {
  return platz === "a" ? "Platz A" : "Platz B";
}

/** Was in der Zeile über einen Platz steht. */
export function platzZustand(belegt: boolean, aufSendung: boolean): { text: string; ton: "live" | "bereit" | "leer" } {
  if (aufSendung && belegt) return { text: "auf Sendung", ton: "live" };
  if (aufSendung && !belegt) return { text: "auf Sendung, aber niemand verbunden", ton: "leer" };
  if (belegt) return { text: "verbunden, bereit", ton: "bereit" };
  return { text: "frei", ton: "leer" };
}

export function UebergabeCard({ slug, canBroadcast }: { slug: string; canBroadcast: boolean }): JSX.Element | null {
  const [zustand, setZustand] = useState<Uebergabe | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [gemeinsam, setGemeinsam] = useState(6);

  const laden = useCallback(async () => {
    try {
      setZustand(await api.uebergabeStatus(slug));
      setFehler(null);
    } catch (err) {
      setFehler(errorText(err));
    }
  }, [slug]);

  useEffect(() => {
    void laden();
    // Alle fünf Sekunden nachsehen: Wer sich anhängt, soll hier auftauchen,
    // ohne dass jemand die Seite neu lädt.
    const t = setInterval(() => void laden(), 5000);
    return () => clearInterval(t);
  }, [laden]);

  if (!canBroadcast || !zustand) return null;
  const { platz_a, platz_b, sendeplatz, angebot, darf, frei, ingest } = zustand;
  const ziel = sendeplatz === "a" ? "b" : "a";
  const zielBelegt = ziel === "a" ? platz_a : platz_b;

  const tun = async (fn: () => Promise<unknown>): Promise<void> => {
    setBusy(true);
    setFehler(null);
    try {
      await fn();
      await laden();
    } catch (err) {
      setFehler(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const zeile = (platz: "a" | "b", belegt: boolean) => {
    const z = platzZustand(belegt, sendeplatz === platz);
    const farbe = z.ton === "live" ? "bg-red-600 text-white" : z.ton === "bereit" ? "badge-success" : "badge-neutral";
    return (
      <div key={platz} className={`flex min-w-0 items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm ring-1 ${z.ton === "live" ? "bg-red-500/[0.05] ring-red-500/25" : "bg-card ring-[var(--border)]"}`}>
        <span className="whitespace-nowrap font-medium text-primary">{platzName(platz)}</span>
        <span className={`badge ${farbe} font-semibold`}>
          {z.ton === "live" && <span className="h-1.5 w-1.5 animate-live-pulse rounded-full bg-white" aria-hidden="true" />}
          {z.text}
        </span>
      </div>
    );
  };

  return (
    <div className="well mt-5 !p-4">
      <div className="section-label mb-2.5">Übergabe an das nächste Studio</div>
      <div className="grid grid-cols-1 gap-2">
        {zeile("a", platz_a)}
        {zeile("b", platz_b)}
      </div>

      {zustand.gemeinsam && (
        <div className="callout callout-warning mt-2.5 !py-2 text-xs">
          Beide sind gerade gemeinsam auf Sendung.
        </div>
      )}

      {angebot && (
        <div className="callout callout-success mt-2.5 !py-2 text-xs">
          {angebot.von} bietet die Übergabe an – bis {new Date(angebot.bis).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })} Uhr.
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1.5 text-xs text-muted">
          gemeinsam
          <input type="number" min={0} max={30} value={gemeinsam} onChange={(e) => setGemeinsam(Math.max(0, Math.min(30, Number(e.target.value) || 0)))}
            className="input !h-8 !w-14 !px-2 tabular-nums" />
          Sek.
        </label>
        <button disabled={busy} onClick={() => void tun(() => api.uebergabeAnbieten(slug, !angebot))}
          className="btn btn-secondary btn-sm">
          {angebot ? "Angebot zurücknehmen" : "Übergabe anbieten"}
        </button>
        <button disabled={busy || !darf || !zielBelegt} onClick={() => void tun(() => api.uebergeben(slug, ziel, gemeinsam))}
          className="btn btn-primary btn-sm"
          title={!zielBelegt ? `Auf ${platzName(ziel)} hängt niemand.` : !darf ? "Erst muss die Übergabe angeboten werden." : ""}>
          Jetzt an {platzName(ziel)} übergeben
        </button>
      </div>

      {ingest && frei && (
        <p className="mt-3 text-xs leading-relaxed text-muted">
          Freier Platz für den Nachfolger: <b>{platzName(frei)}</b> – Mount <code>{ingest.mount}</code> auf {ingest.host}:{ingest.port}.
          Dasselbe Passwort wie oben.
        </p>
      )}
      {!zielBelegt && (
        <p className="mt-1 text-xs leading-relaxed text-muted">
          Solange auf {platzName(ziel)} niemand hängt, bleibt der Knopf aus – umschalten auf einen leeren Platz hieße: Stille, dann Automatik.
        </p>
      )}
      {fehler && <div className="mt-2 text-sm text-red-600 dark:text-red-400">{fehler}</div>}
    </div>
  );
}
